/**
 * VersionHistory — shows version history for a shortlist with diff summaries.
 */

import { useEffect, useState } from "react";
import { apiFetch } from "@/api/client";
import { parseUTC } from "@/api/types";

interface Version {
  id: string;
  version: number;
  diff_summary: string | null;
  created_at: string;
}

interface Props {
  shortlistId: string;
  currentVersion: number;
}

export function VersionHistory({ shortlistId, currentVersion }: Props) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    apiFetch<{ versions: Version[] }>(`/shortlists/${shortlistId}/versions`)
      .then((res) => setVersions(res.versions))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [shortlistId, expanded]);

  if (currentVersion <= 1) return null;

  return (
    <div className="border-t border-space-30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-space-50 hover:bg-space-20 transition-colors"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="font-medium">Version history</span>
        <span className="text-space-40">({currentVersion} versions)</span>
        <svg
          className={`h-3 w-3 ml-auto transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-3">
          {loading ? (
            <p className="text-xs text-space-40 py-2">Loading...</p>
          ) : versions.length === 0 ? (
            <p className="text-xs text-space-40 py-2">No previous versions</p>
          ) : (
            <div className="space-y-1.5">
              {versions.map((v) => {
                const date = parseUTC(v.created_at);
                const timeStr = date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                });
                const dateStr = date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });

                return (
                  <div
                    key={v.id}
                    className="flex items-start gap-2 py-1.5 text-xs"
                  >
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-jove-30 text-jove flex items-center justify-center text-[10px] font-bold shrink-0">
                      {v.version}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-space-50 truncate">
                        {v.diff_summary || "Updated shortlist"}
                      </p>
                      <p className="text-space-40">
                        {dateStr} at {timeStr}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
